import type {
  StaffAnalyticsApi,
  StaffAnalyticsEventsApi,
  StaffAnalyticsExploreApi,
} from "@lafayette/api-client";
import { createContext, type ReactNode, useContext } from "react";

// What the Analytics area needs from outside: the typed client, the clock, the zone of the browser and a way to find the conversation of a
// customer. The admin app supplies the real ones (`admin-runtime.ts`); a test or the development harness supplies fakes.

export type AnalyticsRuntime = Readonly<{
  api: StaffAnalyticsApi;
  /** The Explore tab's reads (CHT-2029): one metric, a selection, the visits of a range. */
  explore: StaffAnalyticsExploreApi;
  /** The Events routes (CHT-2030): the event table, the event detail, a visit step by step and the recent visits. */
  events: StaffAnalyticsEventsApi;
  /** The zone the page cuts days in: the zone of the browser, or Asia/Kolkata when the browser gives none (open point Q9). */
  timeZone: () => string;
  /**
   * The id of the open conversation of the customer, or `null` when there is none. It reads the support view, which the API allows only to a
   * supervisor and the assigned concierge, so a refusal is a refusal of that route. Left out: the "Open conversation" button is not drawn.
   */
  findConversationId?: (
    customerId: string,
    options?: { signal?: AbortSignal },
  ) => Promise<string | null>;
}>;

const RuntimeContext = createContext<AnalyticsRuntime | null>(null);

export function AnalyticsRuntimeProvider({
  value,
  children,
}: Readonly<{ value: AnalyticsRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useAnalyticsRuntime(): AnalyticsRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The analytics pages need an AnalyticsRuntimeProvider.");
  return runtime;
}
